/**
 * The spec schema card's advanced view: every scraped label, the Motzu spec
 * it lands on, and the labels that do not.
 */

import React, { useMemo, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { InlineMessage } from '../../../components/ui/InlineMessage';
import { LoadingState } from '../../../components/ui/LoadingState';
import { StatusChip, StatusTone } from '../../../components/ui/StatusChip';
import {
  BrandSpecLabelMap,
  BrandSpecLabelRow,
  SpecLabelMapVia,
  SpecLabelOutcome,
} from '../../../services/brandDirectoryApi';
import { bvColor, bvType, formatCount, tabular } from '../brandVendorsTokens';
import { BvButton, BvEmptyState } from './BrandVendorsPrimitives';

const VIA_LABEL: Record<SpecLabelMapVia, string> = {
  explicit: 'Brand rule',
  fuzzy: 'Pattern',
  semantic: 'Similar name',
  other: 'Catch-all',
  unmatched: 'Unmatched',
  dropped: 'Not stored',
  stored: 'Already stored',
};

const OUTCOME_LABEL: Record<SpecLabelOutcome, string> = {
  canonical: 'Mapped',
  other: 'Other',
  unmatched: 'Unmatched',
  dropped: 'Not stored',
};

const OUTCOME_TONE: Record<SpecLabelOutcome, StatusTone> = {
  canonical: 'positive',
  other: 'caution',
  unmatched: 'problem',
  dropped: 'neutral',
};

type FilterId = 'all' | 'mapped' | 'unmapped';

const SHOW_CAP = 200;

const DESKTOP_COLUMNS = 'minmax(160px, 1.5fr) minmax(200px, 1.6fr) 108px minmax(96px, 0.7fr) 72px';
// One column on phones (cells stack, header hidden); the five-column table from md up.
const COLUMNS = { xs: 'minmax(0,1fr)', md: DESKTOP_COLUMNS };

// On phones the header row is visually hidden but stays in the accessibility tree,
// and each stacked cell carries its own visible label instead.
const HEADER_VISUALLY_HIDDEN_XS = {
  position: { xs: 'absolute', md: 'sticky' },
  width: { xs: '1px', md: 'auto' },
  height: { xs: '1px', md: 'auto' },
  overflow: { xs: 'hidden', md: 'visible' },
  clip: { xs: 'rect(0 0 0 0)', md: 'auto' },
  whiteSpace: { xs: 'nowrap', md: 'normal' },
} as const;

const MobileCellLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box aria-hidden sx={{ display: { xs: 'block', md: 'none' }, ...bvType.microCaption, color: bvColor.muted }}>
    {children}
  </Box>
);

/** The field on the product document, `specifications.physical.weight`. */
export const canonicalSpecField = (path: string | null): string | null =>
  path ? `specifications.${path}` : null;

const matches = (row: BrandSpecLabelRow, query: string): boolean => {
  if (!query) return true;
  const haystack = [row.label, row.motzu, row.path, canonicalSpecField(row.path), row.sample]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
};

export const SpecSchemaAdvanced: React.FC<{
  map: BrandSpecLabelMap | undefined;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
}> = ({ map, loading, error, onRetry }) => {
  const [filter, setFilter] = useState<FilterId>('all');
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();

  const counts = useMemo(() => {
    const rows = map?.rows || [];
    const mapped = rows.filter((row) => row.outcome === 'canonical').length;
    return { all: rows.length, mapped, unmapped: rows.length - mapped };
  }, [map]);

  const visible = useMemo(() => {
    const rows = (map?.rows || []).filter((row) => {
      if (filter === 'mapped' && row.outcome !== 'canonical') return false;
      if (filter === 'unmapped' && row.outcome === 'canonical') return false;
      return matches(row, query);
    });
    return { rows: rows.slice(0, SHOW_CAP), total: rows.length };
  }, [map, filter, query]);

  if (loading && !map) {
    return <LoadingState message="Reading spec labels…" />;
  }
  if (error) {
    return (
      <Box sx={{ px: '16px', py: '16px' }}>
        <InlineMessage severity="error">
          Spec mapping could not be read. {error.message}
        </InlineMessage>
        <BvButton size="sm" onClick={onRetry}>
          Try again
        </BvButton>
      </Box>
    );
  }
  if (!map || map.productsWithSpecs === 0) {
    return <BvEmptyState headline="No specifications on file for this brand." py="32px" />;
  }

  const filters: Array<{ id: FilterId; label: string; count: number }> = [
    { id: 'all', label: 'All', count: counts.all },
    { id: 'mapped', label: 'Mapped', count: counts.mapped },
    { id: 'unmapped', label: 'Not mapped', count: counts.unmapped },
  ];

  return (
    <Box sx={{ borderTop: `1px solid ${bvColor.borderSubtle}` }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', px: '16px', pt: '12px' }}>
        {filters.map((item) => (
          <BvButton
            key={item.id}
            size="sm"
            tone={filter === item.id ? 'primary' : 'default'}
            onClick={() => setFilter(item.id)}
            aria-pressed={filter === item.id}
          >
            {item.label} {formatCount(item.count)}
          </BvButton>
        ))}
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search labels"
          size="small"
          aria-label="Search spec labels"
          sx={{ flex: '1 1 180px', minWidth: 160, ml: { sm: 'auto' } }}
        />
      </Box>

      {visible.total === 0 ? (
        <BvEmptyState
          headline={query ? `No labels match “${search.trim()}”.` : 'Nothing in this filter.'}
          note={filter === 'unmapped' ? 'Every sampled label reaches a Motzu spec.' : undefined}
          py="32px"
        />
      ) : (
        <Box sx={{ maxHeight: 560, overflow: 'auto', mt: '8px' }}>
          <Box
            role="table"
            aria-label="Spec label mapping"
            sx={{ minWidth: { md: 640 } }}
          >
            <Box
              role="row"
              sx={{
                display: 'grid',
                gridTemplateColumns: COLUMNS,
                gap: '12px',
                px: { xs: 0, md: '16px' },
                py: { xs: 0, md: '8px' },
                ...HEADER_VISUALLY_HIDDEN_XS,
                top: 0,
                bgcolor: bvColor.surface,
                borderBottom: `1px solid ${bvColor.borderSubtle}`,
                ...bvType.microCaption,
                color: bvColor.muted,
              }}
            >
              <Box role="columnheader">Source label</Box>
              <Box role="columnheader">Motzu spec</Box>
              <Box role="columnheader">Status</Box>
              <Box role="columnheader">How</Box>
              <Box role="columnheader" sx={{ textAlign: 'right' }}>
                Products
              </Box>
            </Box>
            {visible.rows.map((row) => {
              const field = canonicalSpecField(row.path);
              return (
              <Box
                role="row"
                key={`${row.label}\0${row.outcome}\0${row.via}\0${row.path ?? ''}\0${row.motzu ?? ''}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: COLUMNS,
                  gap: '12px',
                  alignItems: 'center',
                  px: '16px',
                  py: '8px',
                  borderBottom: `1px solid ${bvColor.borderSubtle}`,
                  minHeight: 48,
                }}
              >
                <Box role="cell" sx={{ minWidth: 0 }}>
                  <MobileCellLabel>Source label</MobileCellLabel>
                  <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink, overflowWrap: 'anywhere' }}>{row.label}</Box>
                  {row.sample && (
                    <Box sx={{ ...bvType.microCaption, color: bvColor.muted, overflowWrap: 'anywhere' }}>
                      Sample · {row.sample}
                    </Box>
                  )}
                </Box>
                <Box role="cell" sx={{ minWidth: 0 }}>
                  <MobileCellLabel>Motzu spec</MobileCellLabel>
                  <Box sx={{ ...bvType.tableSecondary, color: row.motzu ? bvColor.ink2 : bvColor.muted, overflowWrap: 'anywhere' }}>
                    {row.motzu || '—'}
                  </Box>
                  {field && (
                    <Box sx={{ ...bvType.microCaption, color: bvColor.ink3, overflowWrap: 'anywhere' }}>
                      {field}
                    </Box>
                  )}
                  {row.outcome === 'canonical' && !row.written && (
                    <Box sx={{ ...bvType.microCaption, color: bvColor.muted }}>Already filled by another label</Box>
                  )}
                </Box>
                <Box role="cell">
                  <MobileCellLabel>Status</MobileCellLabel>
                  <StatusChip label={OUTCOME_LABEL[row.outcome]} tone={OUTCOME_TONE[row.outcome]} dot />
                </Box>
                <Box role="cell" sx={{ ...bvType.caption, color: bvColor.ink3 }}>
                  <MobileCellLabel>How</MobileCellLabel>
                  {VIA_LABEL[row.via]}
                </Box>
                <Box role="cell" sx={{ ...bvType.caption, color: bvColor.ink, textAlign: { xs: 'left', md: 'right' }, ...tabular }}>
                  <MobileCellLabel>Products</MobileCellLabel>
                  {formatCount(row.products)}
                </Box>
              </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Box sx={{ ...bvType.microCaption, color: bvColor.muted, px: '16px', py: '10px' }}>
        {visible.total > visible.rows.length
          ? `Showing ${formatCount(visible.rows.length)} of ${formatCount(visible.total)}. Search to narrow. `
          : ''}
        {map.truncated ? 'The longest tail of mapped labels is left off this list. ' : ''}
        Each row is one label and one destination, counted once however many products carry it. The percent above counts every label on every sampled product. Measured on{' '}
        {formatCount(map.productsWithSpecs)} of {formatCount(map.productsSampled)} sampled products. The grey “Sample” line is that label’s value on the first product in the sample that carried it.
      </Box>
    </Box>
  );
};
